import { useCallback, useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import type { Member } from '../types';

export interface SessionState {
  session: Session | null;
  member: Member | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

export function useSession(): SessionState {
  const [session, setSession] = useState<Session | null>(null);
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);

  const loadMember = useCallback(async (s: Session | null) => {
    if (!s) { setMember(null); return; }
    // RLS lets you read your own row and nothing else, so this is a safe
    // "am I allowed in yet?" check even while pending.
    const { data } = await supabase
      .from('i18n_member').select('*').eq('id', s.user.id).maybeSingle();
    setMember((data as Member) ?? null);
  }, []);

  useEffect(() => {
    let alive = true;
    supabase.auth.getSession().then(async ({ data }) => {
      if (!alive) return;
      setSession(data.session);
      await loadMember(data.session);
      if (alive) setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange(async (_e, s) => {
      if (!alive) return;
      setSession(s);
      await loadMember(s);
      setLoading(false);
    });
    return () => { alive = false; sub.subscription.unsubscribe(); };
  }, [loadMember]);

  const refresh = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    setSession(data.session);
    await loadMember(data.session);
  }, [loadMember]);

  return { session, member, loading, refresh };
}

export const signInWithGoogle = () =>
  supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: window.location.origin,
      // Google skips its account chooser when exactly one account is signed in
      // and the app is already authorised, which silently signs you in as
      // whoever that is. Reviewers with a personal and a work account need to
      // choose, and so does anyone testing as more than one person.
      queryParams: { prompt: 'select_account' },
    },
  });

export const signOut = () => supabase.auth.signOut();
